import {
  InputCommandSearchCompaniesSelected,
} from "@/components/prospecting/input-search-companies";
import { ClarificationOption } from "@/components/prospecting/search-clarification-chips";
import { MapControlsItineraryState } from "@/components/itinerary/map-controls";
import { toast, useToastError } from "@/components/ui/use-toast";
import { useApi, useApiStream } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { setWindowTitle } from "@/lib/window-title";
import {
  CompanyDTO,
  ForcedSearchTool,
  SavedSearchDTO,
  SearchEngineAgent,
  SearchEngineEmployeeResult,
  SearchEngineResult,
  SearchFeedbackRating,
  SearchMode,
  SearchReasoningEvent,
} from "@shaarp/common";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const SEARCH_MAX_TOTAL_RESULTS = 200;
/** Target number of new results requested per search page ("Search More"). */
const SEARCH_PAGE_SIZE = 50;

/** Keep only the first occurrence of each item by `id`. */
const dedupById = <T extends { id: string }>(items: T[]): T[] =>
  items.filter(
    (item, index, self) =>
      index === self.findIndex((other) => other.id === item.id),
  );

export type PendingClarification = {
  readonly search_id: string;
  readonly options: ClarificationOption[];
};

export type SearchPageState = {
  readonly querySearchValue: string | undefined;
  readonly searchCompaniesSelected: InputCommandSearchCompaniesSelected | undefined;
  readonly setSearchCompaniesSelected: (
    value: InputCommandSearchCompaniesSelected | undefined,
  ) => void;
  readonly isLoadingSearch: boolean;
  readonly isLoadingSavedSearch: boolean;
  readonly notFound: boolean;
  readonly countSearchLaunched: number;
  readonly companies: CompanyDTO[];
  readonly employees: SearchEngineEmployeeResult[];
  readonly searchType: "company" | "employee";
  readonly mainError: Error | null;
  readonly canSearchMore: boolean;
  readonly maxResultsReached: boolean;
  readonly forcedTool: ForcedSearchTool | null;
  readonly setForcedTool: (tool: ForcedSearchTool | null) => void;
  readonly searchMode: SearchMode;
  readonly setSearchMode: (mode: SearchMode) => void;
  readonly isMapOpened: boolean;
  readonly setIsMapOpened: (open: boolean) => void;
  readonly companySelected: CompanyDTO | undefined;
  readonly setCompanySelected: (company: CompanyDTO | undefined) => void;
  readonly itineraryState: MapControlsItineraryState | null;
  readonly setItineraryState: (state: MapControlsItineraryState | null) => void;
  readonly searchEngineResult: SearchEngineResult | undefined;
  readonly reasoningEvents: SearchReasoningEvent[];
  readonly previousSearchAgents: SearchEngineAgent[];
  readonly aiSuggestions: string[];
  readonly setAiSuggestions: (suggestions: string[]) => void;
  readonly savedFeedbackRating: SearchFeedbackRating | null;
  readonly savedFeedbackComment: string | null;
  readonly pendingClarification: PendingClarification | null;
  readonly setPendingClarification: (clarification: PendingClarification | null) => void;
  readonly currentSearchIdRef: React.MutableRefObject<string | undefined>;
  readonly personalizedCapabilities: string[];
  readonly isRelaunch: boolean;
  readonly hasResults: boolean;
  readonly launchNewSearch: (value: InputCommandSearchCompaniesSelected) => void;
  readonly relaunchSearch: () => void;
  readonly cancelSearch: () => void;
  readonly launchSearchCompanies: (
    page?: number,
    searchEngineAgents?: SearchEngineAgent[] | null,
    exceptCompaniesIds?: string[],
    queryOverride?: string,
  ) => Promise<void>;
  readonly launchSearchMore: () => Promise<void>;
};

/**
 * Manages all state, refs, and async logic for the SearchPage component.
 * The component retains only JSX rendering responsibilities.
 */
export function useSearchPageState(): SearchPageState {
  const api = useApi();
  const apiStream = useApiStream();
  const { t } = useTranslation();
  const toastError = useToastError();
  const navigate = useNavigate();
  const location = useLocation();
  const { searchId: routeSearchId } = useParams<{ searchId: string }>();

  const [querySearchValue, setQuerySearchValue] = useState<string>();
  const [searchCompaniesSelected, setSearchCompaniesSelected] =
    useState<InputCommandSearchCompaniesSelected>();
  const [isLoadingSearch, setIsLoadingSearch] = useState<boolean>(false);
  const [isLoadingSavedSearch, setIsLoadingSavedSearch] =
    useState<boolean>(false);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [countSearchLaunched, setCountSearchLaunched] = useState<number>(0);
  const [pageSearch, setPageSearch] = useState<number>(1);
  const [companies, setCompanies] = useState<CompanyDTO[]>([]);
  const [employees, setEmployees] = useState<SearchEngineEmployeeResult[]>([]);
  const [searchType, setSearchType] = useState<"company" | "employee">(
    "company",
  );
  const [mainError, setMainError] = useState<Error | null>(null);
  const [canSearchMore, setCanSearchMore] = useState<boolean>(true);
  const [maxResultsReached, setMaxResultsReached] = useState<boolean>(false);
  const [forcedTool, setForcedTool] = useState<ForcedSearchTool | null>(null);
  const [searchMode, setSearchMode] = useState<SearchMode>("strict");
  const [isMapOpened, setIsMapOpened] = useState<boolean>(false);
  const [companySelected, setCompanySelected] = useState<
    CompanyDTO | undefined
  >(undefined);
  const [itineraryState, setItineraryState] =
    useState<MapControlsItineraryState | null>(null);
  const [searchEngineResult, setSearchEngineResult] = useState<
    SearchEngineResult | undefined
  >();
  const [reasoningEvents, setReasoningEvents] = useState<
    SearchReasoningEvent[]
  >([]);
  const [previousSearchAgents, setPreviousSearchAgents] = useState<
    SearchEngineAgent[]
  >([]);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [savedFeedbackRating, setSavedFeedbackRating] =
    useState<SearchFeedbackRating | null>(null);
  const [savedFeedbackComment, setSavedFeedbackComment] = useState<
    string | null
  >(null);
  const [pendingClarification, setPendingClarification] =
    useState<PendingClarification | null>(null);

  const currentSearchIdRef = useRef<string | undefined>(routeSearchId);
  const companiesCountRef = useRef(0);
  const employeesCountRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const personalizedCapabilities = useMemo(() => {
    if (aiSuggestions.length === 0) return [];
    return aiSuggestions.slice(0, 3).map((query) => {
      const stripped = query
        .replace(/^(Trouve[- ]moi (les|des)|Find (me )?(the )?)/i, "")
        .trim();
      const capitalized = stripped.charAt(0).toUpperCase() + stripped.slice(1);
      return capitalized.endsWith("...") ? capitalized : `${capitalized} ...`;
    });
  }, [aiSuggestions]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const resetStates = () => {
    companiesCountRef.current = 0;
    employeesCountRef.current = 0;
    setCompanies([]);
    setEmployees([]);
    setSearchType("company");
    setCanSearchMore(true);
    setMaxResultsReached(false);
    setCountSearchLaunched(0);
    setMainError(null);
    setIsLoadingSearch(false);
    setSearchEngineResult(undefined);
    setReasoningEvents([]);
    setPreviousSearchAgents([]);
    setSavedFeedbackRating(null);
    setSavedFeedbackComment(null);
    setPendingClarification(null);
  };

  const addCompanies = useCallback((newCompaniesDto: CompanyDTO[]) => {
    setCompanies((prevCompaniesDto) => {
      const all = dedupById([...newCompaniesDto, ...prevCompaniesDto]);
      companiesCountRef.current = all.length;
      return all;
    });
  }, []);

  /**
   * Append streamed employees, deduplicating by id. The backend already serves
   * disjoint windows across "Search More" pages (offset-based continuation),
   * but this guards against the same profile resurfacing under a different
   * Coresignal id or on deeper pages.
   */
  const addEmployees = useCallback(
    (newEmployees: SearchEngineEmployeeResult[]) => {
      setEmployees((prev) => {
        const all = dedupById([...prev, ...newEmployees]);
        employeesCountRef.current = all.length;
        return all;
      });
    },
    [],
  );

  const launchSearchCompanies = async (
    page: number = 1,
    searchEngineAgents: SearchEngineAgent[] | null = null,
    exceptCompaniesIds: string[] = [],
    queryOverride?: string,
    // Explicit mode/tool for this request, threaded past React's stale-closure
    // trap: when a search is (re)launched right after setSearchMode/setForcedTool,
    // this closure still holds the previous state, so the persisted values must
    // be passed in rather than read from `searchMode`/`forcedTool` below.
    modeOverride?: {
      searchMode?: SearchMode;
      forcedTool?: ForcedSearchTool | null;
    },
  ) => {
    const query = queryOverride || querySearchValue;
    if (!(!isLoadingSearch && (query || querySearchValue))) {
      return;
    }

    const effectiveSearchMode = modeOverride?.searchMode ?? searchMode;
    const effectiveForcedTool =
      modeOverride && "forcedTool" in modeOverride
        ? modeOverride.forcedTool
        : forcedTool;

    try {
      if (page > 1 && searchEngineResult?.searchEngineAgents) {
        setPreviousSearchAgents((prev) => [
          ...prev,
          ...searchEngineResult.searchEngineAgents,
        ]);
      }

      setIsLoadingSearch(true);
      setPageSearch(page);
      setCountSearchLaunched((prev) => prev + 1);

      const companiesFetchedUnique: CompanyDTO[] = [];
      // Per-page tallies for the "Search More" toast / canSearchMore logic,
      // tracked for both result types (employee searches stream `employees`).
      let employeesFetchedThisPage = 0;
      let isEmployeeSearch = false;

      await apiStream.post(
        `/search-engine/search`,
        {
          query,
          page: page,
          // Target ~50 results per page (employee role searches collect up to
          // this budget; companies keep their own per-tool defaults below it).
          newItemsCount: SEARCH_PAGE_SIZE,
          previousAgents: searchEngineAgents,
          exceptCompaniesIds,
          searchMode: effectiveSearchMode,
          ...(effectiveForcedTool && { forcedTool: effectiveForcedTool }),
          ...(page > 1 &&
            currentSearchIdRef.current && {
              searchId: currentSearchIdRef.current,
            }),
        },
        {
          onOpen(abortController) {
            abortControllerRef.current = abortController;
          },
          onMessage(eventSourceMessage) {
            if (eventSourceMessage?.data) {
              const event: SearchEngineResult = JSON.parse(
                eventSourceMessage.data,
              );

              setSearchEngineResult(event);

              if ("searchId" in event && event.searchId) {
                currentSearchIdRef.current = event.searchId;
                // The entry's own state, not null: it carries React Router's
                // index, which back and forward are read against.
                window.history.replaceState(
                  window.history.state,
                  "",
                  `/prospecting/search/${event.searchId}`,
                );
              }

              if (event.searchType) {
                setSearchType(event.searchType);
                if (event.searchType === "employee") isEmployeeSearch = true;
              } else if (
                event.searchEngineAgents?.some((a) => a.type === "employee")
              ) {
                setSearchType("employee");
                isEmployeeSearch = true;
              }

              if (
                "employees" in event &&
                event.employees &&
                event.employees.length > 0
              ) {
                addEmployees(event.employees);
                employeesFetchedThisPage += event.employees.length;
              }

              if ("reasoningEvents" in event && event.reasoningEvents) {
                const newEvents = event.reasoningEvents;
                const reasoningEventsForUi: SearchReasoningEvent[] = [];
                for (const reasoningEvent of newEvents) {
                  if (reasoningEvent.type === "clarification_required") {
                    // On "Search More" continuations (page > 1) the user has
                    // already validated their intent, so don't re-prompt for
                    // clarification — just keep streaming additional results.
                    if (page === 1) {
                      setPendingClarification({
                        search_id: reasoningEvent.search_id,
                        options: reasoningEvent.options,
                      });
                    }
                    continue;
                  }
                  reasoningEventsForUi.push(reasoningEvent);
                }
                if (reasoningEventsForUi.length > 0) {
                  setReasoningEvents((prev) => [
                    ...prev,
                    ...reasoningEventsForUi,
                  ]);
                }
              }

              if ("newCompanies" in event && event.newCompanies.length > 0) {
                addCompanies(event.newCompanies);
                companiesFetchedUnique.push(...event.newCompanies);
              }
            }
          },
          onError(err) {
            throw err;
          },
          onClose() {
            abortControllerRef.current = null;

            const totalResults = isEmployeeSearch
              ? employeesCountRef.current
              : companiesCountRef.current;
            const maxReached = totalResults >= SEARCH_MAX_TOTAL_RESULTS;

            if (maxReached) {
              setMaxResultsReached(true);
              setCanSearchMore(false);
            }

            const fetchedThisPage = isEmployeeSearch
              ? employeesFetchedThisPage
              : companiesFetchedUnique.length;

            if (page > 1 && fetchedThisPage > 0) {
              toast({
                title: t("prospecting.search.toast.success.title"),
                description: t("prospecting.search.toast.success.description", {
                  count: fetchedThisPage,
                }),
              });
            } else if (page > 1 && fetchedThisPage === 0) {
              toast({
                title: t("prospecting.search.toast.noMore.title"),
                description: t("prospecting.search.toast.noMore.description"),
              });
              setCanSearchMore(false);
            }
          },
        },
      );
    } catch (error) {
      if ((error as { code?: string })?.code === "DEEP_SEARCH_WEEKLY_LIMIT") {
        toast({
          variant: "destructive",
          title: t("prospecting.search.toast.deepSearchLimit.title"),
          description: t("prospecting.search.toast.deepSearchLimit.description"),
        });
      } else {
        toastError();
      }
      setMainError(error as Error);
      setCanSearchMore(false);
    } finally {
      setIsLoadingSearch(false);
    }
  };

  const startSearch = (
    query: string,
    modeOverride?: {
      searchMode?: SearchMode;
      forcedTool?: ForcedSearchTool | null;
    },
  ) => {
    currentSearchIdRef.current = undefined;
    resetStates();
    setQuerySearchValue(query);
    setSearchCompaniesSelected({ text: query });
    // Reflect the persisted mode in the toggles so the UI (and any later
    // "Search More", which reads state) stays consistent with what actually ran.
    if (modeOverride?.searchMode) setSearchMode(modeOverride.searchMode);
    if (modeOverride && "forcedTool" in modeOverride)
      setForcedTool(modeOverride.forcedTool ?? null);
    if (routeSearchId) {
      navigate("/prospecting/search", { replace: true });
    }
    setTimeout(
      () => launchSearchCompanies(1, null, [], query, modeOverride),
      300,
    );
  };

  // Mode A: Load saved search when navigating to /prospecting/search/:searchId
  useEffect(() => {
    if (!routeSearchId) return;

    const loadSavedSearch = async () => {
      resetStates();
      setIsLoadingSavedSearch(true);
      try {
        const { data: savedSearch } = await api.get<SavedSearchDTO>(
          `/search-engine/search/${routeSearchId}`,
        );

        if (savedSearch.status !== "completed") {
          // Resume the persisted request with its own mode/tool. A pre-strict
          // search (searchMode === null) ran in broad, so resume it that way.
          startSearch(savedSearch.query, {
            searchMode: savedSearch.searchMode ?? "broad",
            forcedTool: savedSearch.forcedTool ?? null,
          });
          return;
        }

        currentSearchIdRef.current = savedSearch.id;
        companiesCountRef.current = savedSearch.companies.length;
        setCompanies(savedSearch.companies);
        setEmployees(savedSearch.employees);
        setSearchType(savedSearch.searchType);
        setReasoningEvents(savedSearch.reasoningEvents);
        setPreviousSearchAgents(savedSearch.searchEngineAgents);
        setSearchEngineResult({
          step: "FINISHED",
          searchEngineAgents: savedSearch.searchEngineAgents,
          newCompanies: savedSearch.companies,
          countExceptCompanies: 0,
          page: 1,
        });
        setCountSearchLaunched(1);
        setQuerySearchValue(savedSearch.query);
        setSearchCompaniesSelected({ text: savedSearch.query });
        setWindowTitle(savedSearch.query);
        if (savedSearch.forcedTool) {
          setForcedTool(savedSearch.forcedTool);
        }
        // A pre-strict search (searchMode === null) ran under broad behavior, so
        // restore it as broad — otherwise a "Search More" would silently switch
        // page 2+ to strict filtering, diverging from the page-1 results shown.
        setSearchMode(savedSearch.searchMode ?? "broad");
        setSavedFeedbackRating(savedSearch.feedbackRating);
        setSavedFeedbackComment(savedSearch.feedbackComment);

        const maxReached =
          savedSearch.companies.length >= SEARCH_MAX_TOTAL_RESULTS;
        if (maxReached) {
          setMaxResultsReached(true);
          setCanSearchMore(false);
        }
      } catch (error) {
        if (
          error &&
          typeof error === "object" &&
          "response" in error &&
          (error as { response?: { status?: number } }).response?.status === 404
        ) {
          setNotFound(true);
          return;
        }
        toastError();
        setMainError(new Error("Failed to load saved search"));
      } finally {
        setIsLoadingSavedSearch(false);
      }
    };

    loadSavedSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSearchId]);

  // Backward compat: handle old ?q= or ?search= links, then clean URL
  useEffect(() => {
    if (routeSearchId) return;
    const params = new URLSearchParams(location.search);
    const query = params.get("q") || params.get("search");
    if (query) {
      window.history.replaceState(
        window.history.state,
        "",
        "/prospecting/search"
      );
      startSearch(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSearchId, location.search]);

  const cancelSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoadingSearch(false);
  };

  const launchNewSearch = (value: InputCommandSearchCompaniesSelected) => {
    startSearch(value.text);
  };

  const relaunchSearch = () => {
    if (querySearchValue) {
      startSearch(querySearchValue);
    }
  };

  const launchSearchMore = async () => {
    await launchSearchCompanies(
      pageSearch + 1,
      searchEngineResult?.searchEngineAgents || null,
      companies.map((company) => company.id),
    );
  };

  const isRelaunch =
    !isLoadingSearch &&
    !isLoadingSavedSearch &&
    countSearchLaunched >= 1 &&
    !!querySearchValue;

  const hasResults =
    countSearchLaunched >= 1 || isLoadingSearch || isLoadingSavedSearch;

  return {
    querySearchValue,
    searchCompaniesSelected,
    setSearchCompaniesSelected,
    isLoadingSearch,
    isLoadingSavedSearch,
    notFound,
    countSearchLaunched,
    companies,
    employees,
    searchType,
    mainError,
    canSearchMore,
    maxResultsReached,
    forcedTool,
    setForcedTool,
    searchMode,
    setSearchMode,
    isMapOpened,
    setIsMapOpened,
    companySelected,
    setCompanySelected,
    itineraryState,
    setItineraryState,
    searchEngineResult,
    reasoningEvents,
    previousSearchAgents,
    aiSuggestions,
    setAiSuggestions,
    savedFeedbackRating,
    savedFeedbackComment,
    pendingClarification,
    setPendingClarification,
    currentSearchIdRef,
    personalizedCapabilities,
    isRelaunch,
    hasResults,
    launchNewSearch,
    relaunchSearch,
    cancelSearch,
    launchSearchCompanies,
    launchSearchMore,
  };
}
